import React, { useEffect, useState } from 'react'
import Text from '../../../../components/Text'
import {
  maxCodeLength,
  minCodeLength,
  showExtendPlan,
  verifyCodeErrorMessage,
  verifyCodeRegex,
  viemaxImgUrls,
} from '../../../../constants/viemax-constants'
import InputField from '../../../../components/InputField'
import useVerifyMedicalCode from '../../hooks/useVerifyMedicalCode'
import VerifyError from './VerifyError'
import * as actions from '../../../../store/slices'
import { useDispatch } from 'react-redux'
import VerifySuccess from './VerifySuccess'
import HaveProductPurchased from './index'
import ProceedButton from '../../../../components/Signup/ProceedButton'

const VerifyCode = ({
  onCodeVerified,
  onMoveNext,
  isPlanExtension = false,
  isLastStep = false,
}) => {
  const { handleVerifyMedicationCode, isLoading, verifyError, data, success } =
    useVerifyMedicalCode()
  const [verifyCode, setVerifyCode] = useState('')
  const [isVerifyErrorOpen, setIsVerifyErrorOpen] = useState(false)
  const [isVerifyCodeValid, setIsVerifyCodeValid] = useState(false)
  const [isVerifySuccessOpen, setIsVerifySuccessOpen] = useState(false)
  const dispatch = useDispatch()

  const handleOnVerifyCode = () => {
    setIsVerifyErrorOpen(false)
    handleVerifyMedicationCode(verifyCode)
  }

  useEffect(() => {
    if (success) {
      if (showExtendPlan) {
        setIsVerifySuccessOpen(true)
      } else {
        handleOnCloseVerifySuccess()
      }
    }
  }, [success])

  const handleOnCloseVerifySuccess = () => {
    if (onCodeVerified) {
      onCodeVerified(verifyCode)
      dispatch(actions.resetVerifyCodeData())
      return
    }
    if (onMoveNext) {
      onMoveNext()
      dispatch(actions.resetVerifyCodeData())
      return
    }
    setIsVerifySuccessOpen(false)
  }

  const handleOnCloseVerifyError = () => {
    setIsVerifyErrorOpen(false)
    setVerifyCode('')
    setIsVerifyCodeValid(false)
    dispatch(actions.resetVerifyCodeData())
  }

  useEffect(() => {
    if (verifyError) {
      setIsVerifyErrorOpen(true)
    }
  }, [verifyError])

  const handleOnChangeVerifyCode = (event) => {
    const value = event.target.value
    setVerifyCode(value?.toUpperCase())
    if (verifyCodeRegex.test(value)) {
      setIsVerifyCodeValid(true)
    } else {
      setIsVerifyCodeValid(false)
    }
  }

  return (
    <div className="min-w-sm max-w-lg pb-[98px] pt-8 relative mx-auto w-full bg-white px-5">
      {showExtendPlan ? (
        <div className="space-y-6">
          <div className="space-y-3">
            <Text
              tag="p"
              variant="headline"
              size="medium"
              className="text-[#1F2933]"
            >
              Enter Zy-Verify Code
            </Text>
            <Text
              tag="p"
              variant="body"
              size="medium"
              className="text-[#475467]"
            >
              Enter the alphanumeric code from the back of the pack and verify
              the code.
            </Text>
          </div>
          <img src={viemaxImgUrls.verifyCodeDemo} alt="verify-code-demo" />

          <div className="pt-5">
            <InputField
              name="verifyCode"
              label="Zy-Verify Code"
              type="text"
              placeholder="Enter Zy-verify code"
              value={verifyCode}
              autoFocus
              error={verifyCode.length > 0 && !isVerifyCodeValid}
              errorMessage={
                verifyCode.length > 0 && !isVerifyCodeValid
                  ? verifyCodeErrorMessage
                  : null
              }
              maxLength={maxCodeLength}
              minLength={minCodeLength}
              onChange={handleOnChangeVerifyCode}
            />
          </div>

          <ProceedButton
            onMoveNext={handleOnVerifyCode}
            isLastStep={isLastStep}
            disabled={!verifyCode || isLoading || !isVerifyCodeValid}
            btnText={isLoading ? 'Verifying...' : 'Verify Code'}
          />
        </div>
      ) : (
        <div className="-mx-4">
          <HaveProductPurchased
            onMoveNext={handleOnVerifyCode}
            isLastStep={isLastStep}
            isLoading={isLoading}
          />
        </div>
      )}

      <VerifyError
        isOpen={isVerifyErrorOpen}
        data={data}
        onRetry={handleOnCloseVerifyError}
        onClose={handleOnCloseVerifyError}
      />

      <VerifySuccess
        planExtension={isPlanExtension}
        isOpen={isVerifySuccessOpen}
        onClose={handleOnCloseVerifySuccess}
      />
    </div>
  )
}

export default VerifyCode
